import Spinner from '@shared/componets/feedback/Spinner/Spinner';
import { useEffect, useState } from 'react';

/**
 * Crea un componente cargable de forma asíncrona con indicador de carga.
 *
 * Resiliente a fallos de red (app de campo/mala señal):
 * - Reintenta el import dinámico ante errores transitorios (ej. ChunkLoadError por señal intermitente).
 * - Si el chunk falla porque la app se actualizó (deploy nuevo → hashes nuevos) y HAY conexión,
 *   recarga la página UNA vez para traer el index/chunks nuevos (con guard anti-loop).
 * - Si igual no carga, muestra un estado de error con "Reintentar" en vez de quedar en blanco/colgado.
 *
 * @param {Function} moduleImport - Función que retorna la promesa de `import()` dinámico.
 * @returns {React.ComponentType}
 */
const RELOAD_KEY = 'chunk_reload_attempt';

const esErrorDeChunk = (err) => {
    const txt = `${err?.name || ''} ${err?.message || ''}`;
    return /ChunkLoadError|Loading chunk|dynamically imported module|Failed to fetch/i.test(txt);
};

const Loadable = (moduleImport, louderPage = true) => {
    let promise = null;

    // Carga el módulo con reintentos ante fallos transitorios.
    const cargar = (reintentos = 2) =>
        moduleImport().catch((err) => {
            if (reintentos > 0) {
                return new Promise((r) => setTimeout(r, 800)).then(() => cargar(reintentos - 1));
            }
            // Chunk viejo por deploy nuevo + hay conexión → recargar una sola vez para traer lo nuevo.
            if (esErrorDeChunk(err) && navigator.onLine && !sessionStorage.getItem(RELOAD_KEY)) {
                sessionStorage.setItem(RELOAD_KEY, '1');
                window.location.reload();
                return new Promise(() => { }); // queda pendiente hasta que recargue
            }
            throw err;
        });

    const preload = () => {
        if (!promise) promise = cargar();
        return promise;
    };

    function LoadableComponent(props) {
        const [Component, setComponent] = useState(null);
        const [error, setError] = useState(false);

        const intentar = () => {
            setError(false);
            setComponent(null);
            cargar()
                .then((mod) => {
                    setComponent(() => mod.default);
                    sessionStorage.removeItem(RELOAD_KEY);
                })
                .catch(() => setError(true));
        };

        useEffect(() => {
            let mounted = true;
            cargar()
                .then((mod) => {
                    if (!mounted) return;
                    setComponent(() => mod.default);
                    sessionStorage.removeItem(RELOAD_KEY);
                })
                .catch(() => { if (mounted) setError(true); });
            return () => { mounted = false; };
        }, []);

        if (error) {
            return (
                <div style={{
                    minHeight: '60dvh', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
                    padding: '2rem', textAlign: 'center', color: '#6b7280',
                }}>
                    <i className="bi bi-wifi-off" style={{ fontSize: '2.25rem', opacity: 0.6 }} />
                    <p style={{ margin: 0 }}>No se pudo cargar esta pantalla. Revisá tu conexión.</p>
                    <button
                        type="button"
                        onClick={intentar}
                        style={{
                            border: 'none', borderRadius: 8, padding: '0.6rem 1.2rem',
                            background: 'var(--color-blue-primary, #1b4332)', color: '#fff', cursor: 'pointer',
                        }}
                    >
                        Reintentar
                    </button>
                </div>
            );
        }

        if (!Component) return (
            <section className={louderPage ? 'loader_page-active' : ''}>
                {louderPage ? <Spinner background='rgba(0, 0, 0, 0)' color="blue" /> : null}
            </section>
        );
        return <Component {...props} />;
    };

    LoadableComponent.preload = preload;
    return LoadableComponent;
};

export default Loadable;
